import { useState, useMemo, useCallback } from 'react';
import {
  Tabs,
  Tab,
  Card,
  CardContent,
  Badge,
  Divider,
  useDocumentTitle,
  LockIcon,
  UploadIcon,
  DescriptionIcon,
  InfoIcon,
} from '@forge/common';
import mockData from '@/data/mock-data.json';
import { useFiles } from '@/hooks/useFiles';
import { useFileStats } from '@/hooks/useFileStats';
import { useApiMutation } from '@/hooks/useApiMutation';
import { filesApi } from '@/api/files';
import type { FileRecord } from '@/api/files';
import MockDataBanner from '@/pages/access-management/components/MockDataBanner';
import FileSidebar from './components/FileSidebar';
import FileUploadZone from './components/FileUploadZone';
import FileTable from './components/FileTable';
import FileDetailSidebar from './components/FileDetailSidebar';
import ForwardDialog from './components/ForwardDialog';

const FileManagementPage = () => {
  useDocumentTitle('File Management — OneRing Portal');

  const [activeTab, setActiveTab] = useState('manage');
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState<FileRecord | undefined>(undefined);
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardFiles, setForwardFiles] = useState<string[]>([]);

  const { files, isLoading, isUsingMockData, reload } = useFiles();
  const { stats, storage } = useFileStats();

  const isOperator =
    mockData.currentUser.roles.includes('Account Manager') ||
    mockData.currentUser.roles.includes('Admin');

  const filteredFiles = useMemo(() => {
    switch (dashboardFilter) {
      case 'sent':
        return files.filter((f) => !f.deleted && f.direction === 'sent');
      case 'received':
        return files.filter((f) => !f.deleted && f.direction === 'received');
      case 'pending':
        return files.filter((f) => !f.deleted && f.status === 'pending');
      case 'starred':
        return files.filter((f) => !f.deleted && f.starred);
      case 'deleted':
        return files.filter((f) => f.deleted);
      default:
        return files.filter((f) => !f.deleted);
    }
  }, [files, dashboardFilter]);

  const deleteMutation = useApiMutation<string[], void>({
    mutationFn: async (fileIds) => {
      if (fileIds.length === 1) {
        await filesApi.deleteFile(fileIds[0]);
      } else {
        await filesApi.bulkDelete(fileIds);
      }
    },
    successMessage: 'File(s) deleted successfully',
    onSuccess: () => {
      reload();
    },
  });

  const starMutation = useApiMutation<string, void>({
    mutationFn: async (fileId) => {
      await filesApi.toggleStar(fileId);
    },
    onSuccess: () => {
      reload();
    },
  });

  const handleRowClick = useCallback((file: FileRecord) => {
    setSelectedFile((prev) => (prev?.id === file.id ? undefined : file));
  }, []);

  const handleForward = useCallback((filesToForward: FileRecord[]) => {
    setForwardFiles(filesToForward.map((f) => f.name));
    setForwardDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    (fileIds: string[]) => {
      deleteMutation.mutate(fileIds);
      setSelectedFile(undefined);
    },
    [deleteMutation],
  );

  const handleToggleStar = useCallback(
    (fileId: string) => {
      starMutation.mutate(fileId);
    },
    [starMutation],
  );

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">File Management</h2>
      <p className="text-sm text-color-tertiary mb-4">Upload, manage, and transfer files across the platform</p>

      {isUsingMockData && (
        <div className="mb-4">
          <MockDataBanner featureName="File management" />
        </div>
      )}

      {isLoading && (
        <div className="mb-4 text-sm text-color-tertiary">Loading files…</div>
      )}

      <Tabs
        value={activeTab}
        onChange={(id) => setActiveTab(id)}
      >
        <Tab id="manage" active={activeTab === 'manage'}>
          Manage
        </Tab>
        <Tab id="transfer" active={activeTab === 'transfer'}>
          Transfer
        </Tab>
      </Tabs>

      <div className="mt-4">
        {activeTab === 'manage' && (
          <div className="flex gap-4">
            <div className="w-64 flex-shrink-0">
              <FileSidebar
                stats={stats}
                storage={storage}
                activeFilter={dashboardFilter}
                onFilterChange={setDashboardFilter}
              />
            </div>
            <div className="flex-1 min-w-0">
              <FileTable
                files={filteredFiles}
                selectedFileId={selectedFile?.id ?? null}
                onRowClick={handleRowClick}
                onForward={handleForward}
                onDelete={handleDelete}
                onToggleStar={handleToggleStar}
              />
            </div>
          </div>
        )}

        {activeTab === 'transfer' && (
          <div className="flex gap-4">
            <div className="flex-1">
              <FileUploadZone onFilesUploaded={reload} />

              <Card className="mt-4">
                <CardContent>
                  <h3 className="text-sm font-bold mb-3">Transfer Guidelines</h3>
                  <div className="flex flex-col gap-2 text-sm">
                    <div className="flex items-start gap-2">
                      <LockIcon />
                      <span>End-to-end encrypted — files are encrypted in your browser before upload using AES-256-GCM and Web Crypto API</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <UploadIcon />
                      <span>Maximum file size: 5 GB per file with chunked upload (4 MB blocks)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <DescriptionIcon />
                      <span>Supported formats: PDF, XLSX, CSV, DOCX, ZIP, and more</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <InfoIcon />
                      <span>Recipients decrypt files locally — the server never sees plaintext data</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="mt-4">
                <CardContent>
                  <h3 className="text-sm font-bold mb-3">How End-to-End Encryption Works</h3>
                  <Divider />
                  <div className="mt-3 flex flex-col gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase text-color-tertiary mb-1">Sending a File</p>
                      <ol className="text-xs flex flex-col gap-1 list-decimal list-inside text-color-secondary">
                        <li>Your browser fetches the recipient&apos;s public key</li>
                        <li>A random AES-256-GCM key is generated in your browser</li>
                        <li>The file is encrypted locally with the AES key</li>
                        <li>The AES key is wrapped with the recipient&apos;s RSA-OAEP public key</li>
                        <li>Encrypted file + wrapped key are uploaded to secure storage</li>
                      </ol>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase text-color-tertiary mb-1">Receiving a File</p>
                      <ol className="text-xs flex flex-col gap-1 list-decimal list-inside text-color-secondary">
                        <li>Authenticate with MFA and download the encrypted file</li>
                        <li>Unlock your private key with your passphrase</li>
                        <li>Your browser unwraps the AES key using your private key</li>
                        <li>The file is decrypted locally and saved to your device</li>
                      </ol>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="w-80 flex-shrink-0">
              <Card>
                <CardContent>
                  <h3 className="text-sm font-bold mb-3">Recent Transfers</h3>
                  <Divider />
                  <div className="flex flex-col gap-2 mt-3">
                    {files
                      .filter((f) => !f.deleted)
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .slice(0, 5)
                      .map((f) => (
                        <div key={f.id} className="flex items-center gap-2 text-xs">
                          <DescriptionIcon />
                          <span className="flex-1 truncate">{f.name}</span>
                          <Badge color={f.direction === 'sent' ? 'green' : 'blue'}>
                            {f.direction === 'sent' ? 'Sent' : 'Received'}
                          </Badge>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      <FileDetailSidebar
        file={selectedFile}
        visible={selectedFile !== undefined}
        onClose={() => setSelectedFile(undefined)}
        onForward={(f) => handleForward([f])}
        onDelete={(id) => handleDelete([id])}
        isOperator={isOperator}
      />

      <ForwardDialog
        open={forwardDialogOpen}
        onClose={() => setForwardDialogOpen(false)}
        fileNames={forwardFiles}
        onSuccess={reload}
      />
    </div>
  );
};

export default FileManagementPage;
